import request from "@/utils/request";
import { AxiosProgressEvent } from "axios";
import { makeAutoObservable } from "mobx";
import { v4 as uuid } from "uuid";
import { getThumb } from "@/utils";
import { getSimpleComposition, getSimpleMedia } from "@/server";
import { Toast } from "react-vant";

export interface IUploadStore {
  uploadList: any[];
  concurrent?: number;
  queue: string[];
}

export interface UploadParams {
  method?: string;
  files: File[];
  folderId?: number;
  projectId?: number;
  count?: number;
}

export interface UseUploadToFolderProps {
  file: File;
  folderId?: number;
  projectId?: number;
  method?: string;
  shooting_theme?: number;
}

export interface UploadProps {
  url: string;
  data?: any;
  onProgress?: (event: AxiosProgressEvent) => void;
}

class UploadStore implements IUploadStore {
  method = "media";
  uploadList: any[] = [];
  projectId = 0;
  folderId = 0;
  activeUploads = 0;
  concurrent = 3; // 最大并发上传数
  queue: string[] = [];

  constructor() {
    makeAutoObservable(this);
  }

  get isFinish() {
    return (
      !this.uploadList.length ||
      this.uploadList.every((item) => item.status === "success")
    );
  }

  async parseFile(file: File) {
    let cover;
    try {
      cover = await getThumb(file);
    } catch (e) {
      console.log("parseError", e);
    }

    if (file.type.startsWith("image")) {
      return {
        fileType: "image",
        cover,
        duration: 0,
        file,
      };
    } else {
      // const duration = await getVideoDuration(file);
      return {
        fileType: "video",
        cover,
        duration: 0,
      };
    }
  }

  addUploadList = async (params: UploadParams) => {
    console.log(
      "adduploadList------------",
      params.count,
      params.files.length,
      this.uploadList.length
    );

    if (
      this.uploadList.length + params.files.length > 5 ||
      params.count + this.uploadList.length + params.files.length > 15
    ) {
      console.log(
        "--------------",
        this.uploadList.length + params.files.length
      );

      if (this.uploadList.length + params.files.length > 5) {
        Toast.info("最多只能上传5个文件");
      } else {
        Toast.info("素材列表最多只能上传15个文件");
      }

      params.files = params.files.slice(
        0,
        Math.min(
          5 - this.uploadList.length,
          15 - params.count - this.uploadList.length
        )
      );
    }

    const uploadTasks = params.files.map((file) => {
      return this.parseFile(file).then(({ fileType, cover, duration }) => ({
        id: uuid(), // 生成唯一的任务 ID
        file,
        fileType,
        cover,
        duration,
        status: "pending", // 初始状态为待上传
        progress: 0, // 初始进度为 0
        error: null, // 初始错误为 null
      }));
    });
    console.log("uploadTasks", uploadTasks);

    // this.uploadList = [...this.uploadList, ...files];
    // alert(params.method);
    this.method = params.method ?? "media";
    this.folderId = params.folderId || 0;
    this.projectId = params.projectId || 0;

    Promise.all(uploadTasks)
      .then((res) => {
        console.log("res", res);

        this.uploadList = [...this.uploadList, ...res];
        res.forEach((item) => {
          this.queue.push(item.id); // 将任务添加到队列中
        });
        this.startQueue();
      })
      .catch((e) => {
        console.log("error", e);
      });
  };

  upload = async (params: UploadProps) => {
    const token = localStorage.getItem("token");
    const res = await request(params.url, {
      data: params.data,
      method: "POST",
      headers: {
        Token: token || "",
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: params.onProgress,
    });

    return res;
  };

  startQueue = () => {
    // 只要队列有任务并且有空余的并发上传位置，就启动上传任务
    while (this.activeUploads < this.concurrent && this.queue.length > 0) {
      const taskId = this.queue.shift();
      if (taskId) {
        this.start(taskId); // 启动上传任务
      }
    }
  };

  getCover = async (id) => {
    const result =
      this.method === "media"
        ? await getSimpleMedia(id)
        : await getSimpleComposition(id);
    if (!result.play_info?.cover_url) {
      return new Promise((resolve) => {
        setTimeout(async () => {
          const result = await this.getCover(id);
          resolve(result);
        }, 500);
      });
    } else {
      return Promise.resolve(result);
    }
  };

  start = async (id): Promise<any> => {
    const task = this.getTaskById(id);
    try {
      const token = localStorage.getItem("token");
      if (!task) return;

      task.status = "uploading"; // 更新任务状态为上传中
      this.activeUploads++; // 增加并发上传数
      const size = task.file.size;
      const fileName = task.file.name.split(".")[0];

      const { data } = await request("/api/v1/space/oss/upload", {
        method: "POST",
        data: {
          method: this.method === "media" ? "media" : "composition",
          name: fileName,
          ...(this.folderId ? { folder_id: this.folderId } : {}),
          ...(this.projectId ? { project_id: this.projectId } : {}),
          size,
          source: 2,
          shooting_theme: 0,
        },
        headers: {
          Token: token,
        },
      });

      const { host, accessid, policy, signature, expire, callback, dir } =
        data.data;

      const newFormData = new FormData();
      newFormData.append("key", `${dir}${uuid()}-${task.file.name}`);
      newFormData.append("callback", callback);
      newFormData.append("OSSAccessKeyId", accessid);
      newFormData.append("policy", policy);
      newFormData.append("Signature", signature);
      newFormData.append("expire", expire);
      newFormData.append("success_action_status", "200");
      newFormData.append("Content-Disposition", `attachment`);
      newFormData.append("file", task.file);
      const uploadData = await this.upload({
        url: host,
        data: newFormData,
        onProgress: (event: AxiosProgressEvent) => {
          if (event.total) {
            const percentComplete = (
              (event.loaded / event.total) *
              100
            ).toFixed(2);
            console.log(percentComplete);
            task.progress = percentComplete;

            // setProgress(percentComplete);
          }
        },
      });
      const { id } = uploadData.data.data;
      task.status = "checking";

      // setStatus("checking");
      const { data: mediaData } = await this.checkUploadStatus(id);
      console.log("data", mediaData);
      task.result = mediaData;

      if (!task.cover) {
        const coverData = await this.getCover(id);
        task.cover = coverData.play_info?.cover_url;
        task.url = coverData.play_info?.play_url;
        if (coverData.duration) {
          task.duration = Math.round(coverData.duration / 1000);
        }
      }

      task.status = "success";
      // setStatus("success");
      // return { id, key: object_key };
    } catch (e) {
      // setStatus("failed");
      task.status = "failed";
      task.error = e;
      console.log("上传失败", e);
    } finally {
      // Taro.hideLoading();
      this.activeUploads--;
      this.startQueue(); // 上传完成后启动下一个任务
    }
  };

  getTaskById(id: string) {
    const task = this.uploadList.find((t) => t.id === id);
    if (task) {
      return task;
    }
    return null;
  }

  checkUploadStatus = async (
    id: number,
    method = "media"
  ): Promise<{ status: boolean; data: any }> => {
    let timer;
    let res, uploadTask;

    this.method === "media"
      ? (res = await request(`/api/v1/space/material/${id}`))
      : (res = await request(`/api/v1/space/composition/${id}`));

    const data = res.data.data;
    uploadTask = data[this.method === "media" ? "material" : "composition"];

    if (uploadTask.status === 3) {
      return Promise.reject({ status: false, data: uploadTask });
    }

    if (uploadTask.status === 2) {
      // 在递归调用中直接返回结果
      return new Promise((resolve, reject) => {
        timer = setTimeout(async () => {
          try {
            const result = await this.checkUploadStatus(id, method);

            resolve(result);
          } catch (error) {
            reject(error);
          }
        }, 1000);
      });
    } else {
      clearTimeout(timer);
      // Taro.showToast({
      //   icon: "success",
      //   title: "上传成功",
      // });
      return Promise.resolve({ status: true, data: uploadTask });
    }
  };
}

const uploadStore = new UploadStore();

export default uploadStore;
